import { Injectable } from '@angular/core';
import { AssetResponseInterface } from '@interfaces/asset/asset.response.interface';
import { AssetRateTypes } from '@interfaces/asset/enums/asset-rate-types.enum';
import { AssetsService } from '@services/assets/assets.service';
import { BasicObservableService } from '@services/basic-observable/basic-observable.service';
import {
  ConfigurationsPurchaseService,
  ConfigurationsSellService,
  ConfigurationsWithdrawalService,
} from '@services/configurations/configurations.service';
import {
  PMOperationMessage,
  PMOperationTypes,
} from '@services/pm-service/pm.models';
import { ExtendedWallet } from '@shared/components/wallet-item-list/wallet-item-list.component';
import { filter, first, lastValueFrom, map, take } from 'rxjs';

@Injectable()
export class OperationsCreatorService {
  private configPurchaseSection = 'configurations-purchase';
  private configSellSection = 'configurations-sell';
  private configWithdrawSection = 'configurations-withdraw';

  wallets$ = this.bOBService.getObservable('actual-page').pipe(
    filter((pages) => !!pages),
    map((page) => page[page.length - 1]?.payload?.wallet),
    first(),
  );

  configurationPurchase$ = this.configurationsPurchaseService
    .getObservable(this.configPurchaseSection)
    .pipe(
      filter((config) => !!config),
      first(),
    );

  configurationSell$ = this.configurationsSellService
    .getObservable(this.configSellSection)
    .pipe(
      filter((config) => !!config),
      first(),
    );

  configurationWithdraw$ = this.configurationsWithdrawService
    .getObservable(this.configWithdrawSection)
    .pipe(
      filter((config) => !!config),
      first(),
    );

  constructor(
    private configurationsPurchaseService: ConfigurationsPurchaseService,
    private configurationsSellService: ConfigurationsSellService,
    private configurationsWithdrawService: ConfigurationsWithdrawalService,
    private bOBService: BasicObservableService,
    private assetsService: AssetsService,
  ) {}

  private async getComissions(
    type: PMOperationTypes,
    assetId: string,
  ): Promise<any> {
    //const _type = type === PMOperationTypes.WITHDRAWAL ? type : 'default';

    const configurationsService = {
      [PMOperationTypes.WITHDRAWAL]: this.configurationsWithdrawService,
      [PMOperationTypes.BUY]: this.configurationsPurchaseService,
      [PMOperationTypes.SELL]: this.configurationsSellService,
    };

    const serviceListener$ = {
      [PMOperationTypes.WITHDRAWAL]: this.configurationWithdraw$,
      [PMOperationTypes.BUY]: this.configurationPurchase$,
      [PMOperationTypes.SELL]: this.configurationSell$,
    };

    const section = {
      [PMOperationTypes.WITHDRAWAL]: this.configWithdrawSection,
      [PMOperationTypes.BUY]: this.configPurchaseSection,
      [PMOperationTypes.SELL]: this.configSellSection,
    };

    const configService = configurationsService[type];
    configService.getAll('', section[type], [assetId]);
    const comissions = await lastValueFrom(serviceListener$[type] as any);
    return comissions as any;
    /* 
    const mappedComissions = {
      [PMOperationTypes.TRANSFER]: (comissions: AssetWithdrawalsConfigInterface) => ({
        min: comissions.minimum,
        max: comissions.decimals,
        comission: comissions.fee
      }),
      [PMOperationTypes.BUY]: (comissions: AssetPurchasesConfigInterface) => ({
        min: comissions.assetMinLimit,
        max: comissions.assetMaxLimit,
        comissions: comissions.buyFee
      }),
      [PMOperationTypes.SELL]: (comissions: AssetPurchasesConfigInterface) => ({
        min: comissions.assetMinLimit,
        max: comissions.assetMaxLimit,
        comissions: comissions.sellFee
      }), 
    }
    return mappedComissions[type](comissions as any)
    */
  }

  getAssetRate(assetId: string, operation?: AssetRateTypes): Promise<any> {
    const assetService = this.assetsService.getRate(assetId, operation).pipe(
      filter((rate) => Array.isArray(rate) && rate.length > 0),
      map((rate) => rate[0]),
      take(1),
    );
    return lastValueFrom(assetService);
  }

  async createOperation(
    type: PMOperationTypes,
    wallet: ExtendedWallet | null,
    asset: AssetResponseInterface,
    props: any = {},
  ): Promise<PMOperationMessage> {
    const user = wallet.user;
    // GET COMISSIONS

    let comissions;
    if (type !== PMOperationTypes.RECEIVE) {
      comissions = await this.getComissions(type, asset.id);
      delete comissions?.asset;
    }

    const rateType = {
      [PMOperationTypes.BUY]: AssetRateTypes.BUY,
      [PMOperationTypes.SELL]: AssetRateTypes.SELL,
    };

    const rate = await this.getAssetRate(asset.id, rateType[type] || null);

    asset.rate.EUR = rate;

    const operation: PMOperationMessage = {
      asset: {
        ...asset,
        assetIcon: 'assets/crypto/' + asset.symbol + '.png',
      },
      wallet: {
        id: wallet?.id,
        createdAt: wallet?.createdAt,
        updatedAt: wallet?.updatedAt,
        balance: Number(wallet?.balance),
        address: wallet?.address,
        incomes: wallet?.incomes,
        user,
      },
      brkUserId: user.id,
      onyzeUserId: user.caasUser,
      operation: {
        type,
        ...comissions,
        ...props,
      },
    };

    return operation;
  }
}
